import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { AdminHeader, Card } from "@/components/admin/ui";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, RefreshCw, Database, CreditCard, Truck, Mail, Image as ImageIcon } from "lucide-react";

const ICONS = {
  "MongoDB": Database,
  "Razorpay": CreditCard,
  "Delhivery": Truck,
  "Resend (Email)": Mail,
  "Image Storage": ImageIcon,
};

export default function AdminIntegrations() {
  const [items, setItems] = useState([]);
  const [checkedAt, setCheckedAt] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/integrations/status");
      setItems(data.integrations);
      setCheckedAt(data.checkedAt);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <div>
      <AdminHeader title="Integrations"
        action={
          <button onClick={load} data-testid="refresh-integrations" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700 border border-slate-200 rounded-lg px-3 py-2 hover:border-amber-400">
            <RefreshCw className="h-4 w-4" /> Re-check
          </button>
        } />
      <p className="text-sm text-slate-500 mb-5">Live connection status for every external service. Secrets are never displayed. Test mode / staging is used for payments and shipping.</p>

      {loading ? (
        <div className="py-20 grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-amber-500" /></div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4" data-testid="integrations-grid">
          {items.map((it) => {
            const Icon = ICONS[it.name] || Database;
            return (
              <Card key={it.name} className="p-5" data-testid={`integration-${it.name.split(" ")[0].toLowerCase()}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <span className="h-10 w-10 rounded-xl bg-slate-100 grid place-items-center text-slate-700"><Icon className="h-5 w-5" /></span>
                    <div>
                      <h3 className="font-semibold text-slate-900">{it.name}</h3>
                      <p className="text-xs text-slate-500 mt-0.5 uppercase tracking-wide">{it.environment}</p>
                    </div>
                  </div>
                  {it.connected ? (
                    <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-semibold bg-emerald-50 px-2.5 py-1 rounded-full"><CheckCircle2 className="h-3.5 w-3.5" /> Connected</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-slate-500 text-xs font-semibold bg-slate-100 px-2.5 py-1 rounded-full"><XCircle className="h-3.5 w-3.5" /> Not connected</span>
                  )}
                </div>
                <p className="text-sm text-slate-600 mt-3">{it.configStatus}</p>
              </Card>
            );
          })}
        </div>
      )}
      {checkedAt && <p className="text-xs text-slate-400 mt-4">Last checked: {new Date(checkedAt).toLocaleString()}</p>}
    </div>
  );
}
